import React, { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { signup } from 'src/redux/actions/auth.action';
import {
  resolveDefaultSignupRole,
  resolveTeamInvite,
} from 'src/redux/actions/company.action';
import { logoutSuccess } from 'src/redux/reducers/auth.slice';
import { IconArrowRight, IconBuilding, IconMail } from '../components/AuthIcons';
import PasswordInput from '../components/PasswordInput';

const schema = yup.object().shape({
  fName: yup.string().required('First name is required'),
  lName: yup.string().required('Last name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  companyID: yup.string().required('Company ID is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

const notifySkip = (message) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 3000,
  });

function RegisterForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get('invite');
  const { isLoading, error2, modalVisible } = useSelector((state) => state.login);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      fName: '',
      lName: '',
      email: '',
      companyID: id || '',
      password: '',
      passwordConfirm: '',
    },
  });

  useEffect(() => {
    if (id) {
      setValue('companyID', id);
    }
  }, [id, setValue]);

  const onSubmit = async (model) => {
    const name = `${model.fName} ${model.lName}`;
    const companyID = model.companyID || getValues('companyID');
    let workspaceRole = 'MEMBER';
    let inviteRefPath = null;

    if (inviteToken) {
      const invite = await resolveTeamInvite({
        companyID,
        inviteToken,
        email: model.email,
      });
      if (invite.expired) {
        notifySkip('This invite has expired. Ask an admin to invite you again.');
        return;
      }
      if (invite.inviteMismatch) {
        notifySkip('This invite was sent to a different email address.');
        return;
      }
      if (invite.invalid) {
        notifySkip('This invite is invalid or no longer pending.');
        return;
      }
      workspaceRole = invite.workspaceRole || 'MEMBER';
      inviteRefPath = invite.inviteRefPath || null;
    } else {
      workspaceRole = await resolveDefaultSignupRole(companyID);
    }

    dispatch(
      signup(
        {
          name,
          email: model.email,
          phone: '',
          password: model.password,
          companyID: companyID || model.companyID,
          workspaceRole,
        },
        history,
        notifySkip,
        { inviteRefPath }
      )
    );
  };

  return (
    <>
      <ToastContainer />
      {error2 && (
        <div className="auth-alert auth-alert-error" role="alert">
          {error2}
          <button
            type="button"
            onClick={() => dispatch(logoutSuccess())}
            style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700 }}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}
      {modalVisible && (
        <div className="auth-alert auth-alert-success" role="status">
          Account created. Check your email, then{' '}
          <Link
            className="link"
            to={
              inviteToken && (id || getValues('companyID'))
                ? `/login?invite=${inviteToken}&companyID=${encodeURIComponent(id || getValues('companyID'))}`
                : '/login'
            }
          >
            log in
          </Link>
          .
        </div>
      )}

      {inviteToken && (
        <div className="auth-alert" role="status" style={{ marginBottom: 12 }}>
          Already have an account?{' '}
          <Link
            className="link"
            to={`/login?invite=${inviteToken}&companyID=${encodeURIComponent(id || '')}`}
          >
            Log in to accept this invite
          </Link>
          .
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row2">
          <div className="field">
            <label htmlFor="fName">First name</label>
            <input
              id="fName"
              type="text"
              placeholder="Jordan"
              className={`inp ${errors.fName ? 'inp-error' : ''}`}
              {...register('fName')}
            />
            {errors.fName && <span className="field-error">{errors.fName.message}</span>}
          </div>
          <div className="field">
            <label htmlFor="lName">Last name</label>
            <input
              id="lName"
              type="text"
              placeholder="Avery"
              className={`inp ${errors.lName ? 'inp-error' : ''}`}
              {...register('lName')}
            />
            {errors.lName && <span className="field-error">{errors.lName.message}</span>}
          </div>
        </div>

        <div className="field">
          <label htmlFor="email">Work email</label>
          <div className="ctrl">
            <span className="lead">
              <IconMail />
            </span>
            <input
              id="email"
              type="email"
              placeholder="you@company.com"
              className={`inp has-lead ${errors.email ? 'inp-error' : ''}`}
              {...register('email')}
            />
          </div>
          {errors.email && <span className="field-error">{errors.email.message}</span>}
        </div>

        <div className="field">
          <label htmlFor="companyID">Company ID</label>
          <div className="ctrl">
            <span className="lead">
              <IconBuilding />
            </span>
            <input
              id="companyID"
              type="text"
              placeholder="e.g. NURT-2049"
              disabled={Boolean(id)}
              className={`inp has-lead ${errors.companyID ? 'inp-error' : ''}`}
              {...register('companyID')}
            />
          </div>
          {errors.companyID && <span className="field-error">{errors.companyID.message}</span>}
        </div>

        <div className="row2">
          <PasswordInput
            id="password"
            label="Password"
            placeholder="••••••••"
            error={errors.password?.message}
            {...register('password')}
          />
          <PasswordInput
            id="passwordConfirm"
            label="Confirm"
            placeholder="••••••••"
            error={errors.passwordConfirm?.message}
            {...register('passwordConfirm')}
          />
        </div>

        <button className="btn-primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Creating account…' : 'Create account'}
          <IconArrowRight />
        </button>
      </form>
    </>
  );
}

export default RegisterForm;
